import {
  AfterViewInit,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ResizeService } from './@shared/services/resize/resize.service';
import { ResizeableComponent } from './@shared/components/design/resizeable/resizeable.component';
import {
  Language,
  LanguageService,
} from './@shared/services/language/language.service';
import {
  SKIP_LINKS_NAV_LABEL,
  SKIP_TO_MAIN_CONTENT,
  SKIP_TO_SIDE_PANEL,
  SKIP_TO_TOP_NAV,
} from './@shared/components/skip-links/skip-links.constants';
import { ToastService } from './@shared/services/toast/toast.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent
  extends ResizeableComponent
  implements OnInit, AfterViewInit
{
  @ViewChild('firstSkipLink', { static: true })
  firstSkipLink?: ElementRef<HTMLAnchorElement>;

  skipNavLabel = SKIP_LINKS_NAV_LABEL.hebrew;
  skipMain = SKIP_TO_MAIN_CONTENT.hebrew;
  skipTop = SKIP_TO_TOP_NAV.hebrew;
  skipSide = SKIP_TO_SIDE_PANEL.hebrew;

  constructor(
    protected override readonly resizeService: ResizeService,
    private readonly languageService: LanguageService,
    private readonly toastService: ToastService
  ) {
    super(resizeService)
  }

  override ngOnInit(): void {
    this.toastService.initializeLiveRegions();
    this.applySkipLinkLanguage(this.languageService.getSelectedLanguage());
    this.languageService.selectedLanguage$.subscribe((language) => {
      const body = document.body;
      const html = document.documentElement;

      html.lang = language?.symbol ?? 'en';
      // a11y: audit required dir on <html>. Mirror direction at the document
      // root (the highest ancestor possible) so SR/AT and CSS logical
      // properties see RTL/LTR from the very top of the tree, not only on
      // deeper containers like mat-drawer-container.
      html.dir = language?.direction === 'rtl' ? 'rtl' : 'ltr';

      body.classList.remove('rtl', 'ltr');

      if (language?.direction === 'rtl') {
        body.classList.add('rtl');
      } else {
        body.classList.add('ltr');
      }

      this.applySkipLinkLanguage(language);
    });
  }

  ngAfterViewInit(): void {
    // Nudge focus into the page when the document still has only the default target
    // (first Tab then hits skip links / content — we cannot move focus out of browser chrome).
    queueMicrotask(() => this.tryFocusFirstSkipLink());
  }

  private tryFocusFirstSkipLink(): void {
    const el = this.firstSkipLink?.nativeElement;
    if (!el) {
      return;
    }
    const active = document.activeElement as Node | null;
    if (
      active &&
      active !== document.body &&
      active !== document.documentElement
    ) {
      return;
    }
    el.focus({ preventScroll: true });
  }

  onSkipLinkClick(event: MouseEvent, targetId: string): void {
    event.preventDefault();
    this.moveSkipTargetIntoView(targetId);
  }

  /** Native <a> does not follow Space like a button; mirror button behavior for keyboard users. */
  onSkipLinkSpace(event: Event, targetId: string): void {
    event.preventDefault();
    this.moveSkipTargetIntoView(targetId);
  }

  /**
   * Scroll + move focus without changing the URL (avoids base-href + hash full reload).
   * Retries briefly while lazy routes attach the landmark nodes.
   */
  private moveSkipTargetIntoView(targetId: string, attempt = 0): void {
    const el = document.getElementById(targetId);
    if (el instanceof HTMLElement) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      el.focus({ preventScroll: true });
      return;
    }
    if (attempt < 30) {
      setTimeout(() => this.moveSkipTargetIntoView(targetId, attempt + 1), 50);
    }
  }

  private applySkipLinkLanguage(language: Language | null): void {
    const key = (language?.language ?? 'hebrew') as keyof typeof SKIP_LINKS_NAV_LABEL;
    this.skipNavLabel = SKIP_LINKS_NAV_LABEL[key] ?? SKIP_LINKS_NAV_LABEL.hebrew;
    this.skipMain = SKIP_TO_MAIN_CONTENT[key] ?? SKIP_TO_MAIN_CONTENT.hebrew;
    this.skipTop = SKIP_TO_TOP_NAV[key] ?? SKIP_TO_TOP_NAV.hebrew;
    this.skipSide = SKIP_TO_SIDE_PANEL[key] ?? SKIP_TO_SIDE_PANEL.hebrew;
  }
}
